import StateStorageUtil from './StateStorageUtil';
import {createState, createUniqueID} from './stateFns';
import {merger} from './mergerFns';

/**
 * ExtendableState is a module that utilize the StateStorage functionality.
 *
 * It is created in order to be extended by other components/modules (hence the name "ExtendableState"),
 * therefore, giving a component/module the ability to have a state (Stateful component).
 *
 * The module generate a unique ID and pass it to all of the $.stateStorage functions.
 * Once a state change is occurred an observer event is dispatched with key=${ID}.STATE_CHANGED,
 * and a component which extends this module could override the event callback.
 * (ExtendableState.prototype.stateChangedCallback)
 *
 * @param componentName {string|{customID}}
 * @param [overrideMerger] {function}
 * @constructor
 */
class ExtendableState {
    constructor(overrideMerger = merger) {
        Object.defineProperty(this, '__uniqueID__', {
            value: `${this.constructor.name}_${createUniqueID()}`,
            writable: false,
            configurable: false,
            enumerable: false
        });

        Object.defineProperty(this, '__merger__', {
            value: overrideMerger,
            writable: false,
            configurable: false,
            enumerable: false
        });

        StateStorageUtil.setState(this.getID(), createState());
    }

    stateChangedCallback() {}

    getID() {
        return this.__uniqueID__;
    }

    getMerger() {
        return this.__merger__;
    }

    getStateByIndex(index) {
        return StateStorageUtil.getStateByIndex(this.getID(), index);
    }

    getPrevState() {
        return StateStorageUtil.getPrevState(this.getID());
    }

    getState() {
        return StateStorageUtil.getState(this.getID());
    }

    getValue(key) {
        return StateStorageUtil.getValue(this.getID(), key);
    }

    getValues(valueNames) {
        const len = valueNames.length;

        if (!(valueNames && len))
            throw new TypeError('`valueNames` must be of type array!');

        return this.getState().filter((v, k) => valueNames.includes(k)).toJS();
    }

    setValue(key, data) {
        let bool;

        data = (typeof data === 'function') ? data(this.getValue(key)) : data;
        bool = StateStorageUtil.setValue(this.getID(), key, data);

        this.stateChangedCallback.call(this);

        return bool;
    }

    setState(obj) {
        const bool = StateStorageUtil.setState(this.getID(), obj);

        this.stateChangedCallback.call(this, true);

        return bool;
    }

    updateState(obj, overrideMerger) {
        const bool = StateStorageUtil.updateState(this.getID(), obj, overrideMerger || this.getMerger());

        this.stateChangedCallback.call(this, true);

        return bool;
    }
}
 export default ExtendableState;